import { useMemo, useState } from "react";

import { Container } from "components";
import { DashboardScope } from "types";

import AgentsSection from "../admin/AgentsSection";
import BankStatusSection from "../admin/BankStatusSection";
import ConversionSection from "../admin/ConversionSection";
import OverdueAlertSection from "../admin/OverdueAlertSection";
import CalendarSection from "./CalendarSection";
import DailyStatusSection from "./DailyStatusSection";
import MessagesSection from "./MessagesSection";
import MonthlyProgressSection from "./MonthlyProgressSection";
import OperativeSection from "./OperativeSection";
import TeamKpisSection from "./TeamKpisSection";
import TodoSection from "./TodoSection";
import shellClasses from "./SectionShell.module.css";
import classes from "./TeamLeadDashboard.module.css";
import { buildDashboardParams, DashboardParamsPeriod } from "../shared/dashboardParams";

const joinClasses = (...values: Array<string | false | null | undefined>) =>
  values.filter(Boolean).join(" ");

interface TeamLeadDashboardProps {
  scope?: DashboardScope;
}

const TeamLeadDashboard = ({ scope = "admin" }: TeamLeadDashboardProps) => {
  const [period, setPeriod] = useState<DashboardParamsPeriod>("YEARLY");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedAgentIds, setSelectedAgentIds] = useState<number[] | null>(null);

  const dashboardParams = useMemo(
    () => {
      const params = buildDashboardParams(period, dateFrom, dateTo);

      if (!params || !selectedAgentIds?.length) {
        return params;
      }

      return {
        ...params,
        agentIds: selectedAgentIds,
      };
    },
    [dateFrom, dateTo, period, selectedAgentIds],
  );

  return (
    <Container>
      <div className={classes.page}>
        <div className={classes.layout}>
          <OperativeSection
            className={classes.operative}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
            onPeriodChange={setPeriod}
            params={dashboardParams}
            period={period}
            scope={scope}
          />

          <DailyStatusSection
            className={classes.dailyStatus}
            selectedAgentIds={selectedAgentIds}
          />

          <OverdueAlertSection
            className={classes.overdueCard}
            scope={scope}
            selectedAgentIds={selectedAgentIds}
          />

          <TeamKpisSection
            className={classes.teamKpis}
            selectedAgentIds={selectedAgentIds}
          />

          <AgentsSection
            className={classes.agents}
            scope={scope}
            selectedAgentIds={selectedAgentIds}
            onSelectionChange={setSelectedAgentIds}
          />

          <TodoSection className={classes.todo} />

          <MessagesSection className={classes.messages} />

          <ConversionSection
            className={classes.conversion}
            params={dashboardParams}
            scope={scope}
          />

          <MonthlyProgressSection
            className={classes.progress}
            scope={scope}
            selectedAgentIds={selectedAgentIds}
          />

          <BankStatusSection
            className={classes.banks}
            scope={scope}
            selectedAgentIds={selectedAgentIds}
          />

          <CalendarSection
            className={joinClasses(shellClasses.root, classes.calendar)}
            compact
            selectedAgentIds={selectedAgentIds}
          />
        </div>
      </div>
    </Container>
  );
};

export default TeamLeadDashboard;
